import * as React from 'react';

// Named "bridge" because it exists solely to connect sibling components that
// render in separate slots of ChartLayout and therefore can't share state by
// prop-drilling: the sidebar axis-switcher picker, the chart (HitsWidget), the
// export controls, and the Table/CSV view. Each reads or writes a slice here:
//   - options / selected — the axis-switcher: HitsWidget publishes the available
//     X-axis fields from the current rows; the sidebar picker reads them and
//     writes the selection; HitsWidget reads the selection back.
//   - subtitle — the exported-image subtitle, computed by HitsWidget (which has
//     the filtered rows) and read by the export controls (README 1.1).
//   - rows — the current filtered rows (published by ResultsBridge) so the Table
//     view and CSV export reflect the selection, not the full payload.
// It is deliberately NOT named after any one of these concerns, since it carries
// all four.
//
// The default (no provider) is inert so the shared ChartLayout and the Typesense
// path — which do not use these features — never crash.
// `rows` is null — NOT [] — until ResultsBridge publishes. The two states mean
// different things: null is "no selection available" (Typesense, or before the
// first search), while [] is a real, empty result set. Collapsing them makes a
// zero-result filter fall back to the full injected export, so the Table view
// would show every row precisely when the user filtered down to none.
const EMPTY = {
  options: [], selected: null, subtitle: '', rows: null, initialSelected: null,
  setOptions() {}, setSelected() {}, setSubtitle() {}, setRows() {},
};

const ChartBridgeContext = React.createContext(EMPTY);

export function ChartBridgeProvider({ children, initialSelected = null }) {
  const [options, setOptions] = React.useState([]);
  const [selected, setSelected] = React.useState(initialSelected);
  const [subtitle, setSubtitle] = React.useState('');
  const [rows, setRows] = React.useState(null);

  // `initialSelected` is exposed as well as seeded: consumers that hold the
  // shared-permalink axis until it becomes selectable must key off the ORIGINAL
  // permalink value. Reading live `selected` instead re-captures whatever is
  // current whenever the consumer remounts (e.g. the Chart/Table toggle swaps
  // the chart out), which would pin that value permanently.
  const value = React.useMemo(
    () => ({ options, selected, subtitle, rows, initialSelected, setOptions, setSelected, setSubtitle, setRows }),
    [options, selected, subtitle, rows, initialSelected]
  );

  return <ChartBridgeContext.Provider value={value}>{children}</ChartBridgeContext.Provider>;
}

export function useChartBridge() {
  return React.useContext(ChartBridgeContext);
}
